/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, Card, Grid, Stack } from "@mui/material";
import { useAsyncDebounce, useGlobalFilter, usePagination, useSortBy, useTable } from "react-table";

import SoftBox from "components/SoftBox";
import SoftInput from "components/SoftInput";
import TableCommon from "components/TableCommon";
import SoftTypography from "components/SoftTypography";
import { UserService } from "services/userService";
import ActionCell from "./action-cell";
import SoftBadge from "components/SoftBadge";
import { ACCOUNT_ENUM } from "enums/account-type";

const genColor = (value) => {
  switch (value) {
    case ACCOUNT_ENUM.SUPER_ADMIN:
      return "primary";
    case ACCOUNT_ENUM.CS:
      return "info";
    case ACCOUNT_ENUM.ADMIN:
      return "warning";
    case ACCOUNT_ENUM.USER:
      return "secondary";
    default:
      return "secondary";
  }
};

const renderBadge = (content, color) => {
  return (
    <SoftBadge
      variant="contained"
      color={color}
      size="sm"
      badgeContent={content}
      circular
      container
    />
  );
};

const tableData = {
  columns: [
    {
      Header: "Email",
      accessor: "email",
    },
    {
      Header: "Công ty",
      accessor: "company",
      Cell: ({ value: [name, image] }) => (
        <Box display="flex" gap={1} alignItems="center">
          <Box width={50} height={50}>
            <img
              style={{ width: "100%", height: "100%", objectFit: "contain", borderRadius: 12 }}
              src={image || "./upload.png"}
              alt={name}
              referrerPolicy="no-referrer"
              loading="lazy"
            />
          </Box>
          <Box>{name}</Box>
        </Box>
      ),
    },
    {
      Header: "Role",
      accessor: "role",
    },
    {
      Header: "Actions",
      accessor: "action",
      align: "right",
    },
  ],
  rows: [],
};

const renderData = (items, setDataTable) => {
  return items.map((item) => ({
    id: item._id,
    email: item.email,
    company: [item?.company.name, item?.company.logo],
    role: renderBadge(item.role, genColor(item.role)),
    action: <ActionCell key={item._id} item={item} setDataTable={setDataTable} />,
  }));
};

export default function TableUser({ refetch }) {
  const { id } = useParams();
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState({ sortBy: "createdAt", sortType: "desc", searchBy: "" });
  const [paginationData, setPaginationData] = useState({
    page: 1,
    count: 0,
    size: 5,
  });
  const [table, setTable] = useState(tableData);

  useEffect(() => {
    (async () => {
      try {
        const {
          data: { items, paginate },
        } = await UserService.getAll({
          ...query,
          limit: paginationData.size,
          page: paginationData.page,
          company: id,
        });
        setPaginationData((prev) => ({ ...prev, count: paginate.count, size: paginate.size }));
        setTable((prev) => ({
          ...prev,
          rows: renderData(items, setTable),
        }));
      } catch (error) {
        console.error(error);
      }
    })();
  }, [query, paginationData.page, refetch]);

  const data = useMemo(() => table.rows, [table]);
  const columns = useMemo(() => table.columns, [table]);

  const tableInstance = useTable(
    { columns, data, initialState: { pageIndex: 0 } },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const onSearchChange = useAsyncDebounce((value) => {
    setQuery((prev) => ({ ...prev, searchBy: value }));
  }, 300);

  return (
    <Grid container justifyContent="center" sx={{ height: "100%", mt: 4 }}>
      <Grid item xs={12} lg={12}>
        <Card>
          <SoftBox p={2}>
            <SoftBox lineHeight={0}>
              <SoftTypography variant="h5" fontWeight="bold">
                Danh sách người dùng (user)
              </SoftTypography>
              <SoftBox
                display="flex"
                flexDirection="column"
                justifyContent="space-between"
                alignItems="flex-start"
                pb={1}
                pt={3}
              >
                <SoftBox lineHeight={1}>
                  <Stack spacing={2} direction="row">
                    <SoftBox display="flex" flexDirection="column">
                      <SoftTypography
                        component="label"
                        variant="caption"
                        fontWeight="bold"
                        mb={0.5}
                      >
                        Tìm kiếm
                      </SoftTypography>
                      <SoftBox width="12rem">
                        <SoftInput
                          placeholder="Search..."
                          value={search}
                          onChange={({ currentTarget }) => {
                            setSearch(currentTarget.value);
                            onSearchChange(currentTarget.value);
                          }}
                        />
                      </SoftBox>
                    </SoftBox>
                  </Stack>
                </SoftBox>
              </SoftBox>

              <TableCommon
                table={table}
                query={query}
                loading={loading}
                setQuery={setQuery}
                tableInstance={tableInstance}
                paginationData={paginationData}
                setPaginationData={setPaginationData}
              />
            </SoftBox>
          </SoftBox>
        </Card>
      </Grid>
    </Grid>
  );
}
